import React, { Component } from 'react';
import _ from 'lodash';
import {
    getFraud as fetchFraud,
    fetchUrl
} from './fraudActions';
import { getDoAnySelectedMerchantsHaveFraud } from '../MerchantSelector/merchantSelectorViewState';
import {
    getFraud,
    getFraudCount,
    getPages,
    getFraudErrorMessage,
    getIsFraudLoading,
    getHasMore
} from './fraudEntity';
import FraudListItem from './FraudListItem/FraudListItem';
import { selectTransaction } from '../Transactions/transactionsActions';
import {
    cardTypesMapper,
    doesPropExistAndHasItChanged,
    processMethodMapper,
    transactionTypeMapper,
    getCurrencyCode,
    dataTransform
} from '@nexio/emvio-util-app';
import ColorDollarAmountCell from '../Table/Cells/ColorDollarAmountCell/ColorDollarAmountCell';
import AmountFilter from '../Table/Filters/AmountFilter/AmountFilter';
import {
    transformCardTypeCell,
    transformDateCell,
    transformEllipsisNumberCell,
    transformGatewayTypeCell,
    transformProcessMethodCell,
    transformTransactionStatusCell,
    transformTransactionTypeCell,
    transformUppercaseCell,
    transformCurrencyCodeCell
} from '../../utils/cell';
import FilterWithMapper from '../Table/Filters/FilterWithMapper/FilterWithMapper';
import moment from 'moment';
import Table from '../Table/Table';
import { getDoesUserHaveWriteAccessForMerchant } from '../Self/selfEntity';
import Actions from '../Transactions/Actions/Actions';
import { bulkVoidTransactions } from '../Void/voidActions';
import { bulkCaptureTransactions } from '../Capture/captureActions';
import {
    getBulkVoidErrorMessage,
    getIsBulkVoidLoading,
    getIsBulkVoidSuccessful
} from '../Void/voidEntity';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { toastIfError } from '../../utils/toast';
import { toast } from 'react-toastify';
import {
    getBulkCaptureErrorMessage,
    getIsBulkCaptureLoading,
    getIsBulkCaptureSuccessful
} from '../Capture/captureEntity';
import CurrencyCodeFilter from '../Table/Filters/CurrencyCodeFilter/CurrencyCodeFilter';
import { prettifyDate } from '../../utils/string';
import { getTimezone } from '../UserSettings/userSettingsEntity';
import Marketing from './Marketing/Marketing';
import './Fraud.scss';
import { getGateways } from '../Gateways/gatewaysEntity';
import { getSortedGatewayAndApmMapperArray } from '../../utils/gateways';
import { getApms } from '../Apms/apmEntity';
import config from '../../config/config';
import InPageMerchantSelector from '../MerchantSelector/InPageMerchantSelector';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { getSelf } from '../Self/selfEntity';

export const fraudMapper = {
    R: 'REVIEW',
    D: 'DECLINED',
    A: 'APPROVED'
};

export const hoursToAutoApproveTransaction = 48;

const toolTipContent = <div>Tip: Begin search with ... to search<br />the last four. Example: ...1234</div>;

export function getHoursLeftToAutoApprove(transactionDate) {
    const transactionAutoApproveTime = moment(transactionDate).add(hoursToAutoApproveTransaction, 'hours');
    const hoursLeftToAutoApprove = transactionAutoApproveTime.diff(moment(), 'hours');

    if (hoursLeftToAutoApprove < 0) {
        return '';
    }

    return (hoursLeftToAutoApprove > 1) ? `${hoursLeftToAutoApprove} hours` : '1 hour';
}

export class Fraud extends Component {
    static propTypes = {
        bulkCaptureError: PropTypes.string,
        bulkVoidError: PropTypes.string,
        doAnySelectedMerchantsHaveFraud: PropTypes.bool,
        doesUserHaveWriteAccess: PropTypes.func,
        isBulkCaptureLoading: PropTypes.bool,
        isBulkCaptureSuccessful: PropTypes.bool,
        isBulkVoidLoading: PropTypes.bool,
        isBulkVoidSuccessful: PropTypes.bool,
        timezone: PropTypes.string,
        self: PropTypes.any
    };

    constructor(props) {
        super(props);

        this.resetNeedsRefetch = this.resetNeedsRefetch.bind(this);
        this.refetchData = this.refetchData.bind(this);
        this.shouldRowBeCheckable = this.shouldRowBeCheckable.bind(this);
        this.renderHeaderComponent = this.renderHeaderComponent.bind(this);

        this.state = { needsRefetch: false };

        this.defaultColumnsAndProps = [
            {
                accessor: 'action',
                Cell: (props) => (<Actions transaction={props.original} onRefetch={this.refetchData} />),
                className: ['cursor-pointer', 'no-padding'],
                filterable: false,
                label: 'Action',
                minWidth: 70,
                shouldNotExport: true,
                show: true,
                sortable: false
            },
            {
                accessor: 'merchantName',
                className: ['cursor-pointer'],
                filterable: false,
                label: 'Merchant',
                minWidth: 150,
                show: true,
                sortable: false
            },
            {
                accessor: 'transactionDate',
                Cell: transformDateCell,
                transformData: dataTransform.dateTimezoneButHideTimezone,
                transformType: 'dateTimezoneButHideTimezone',
                className: ['cursor-pointer'],
                defaultSortDesc: true,
                filterable: false,
                label: 'Date',
                minWidth: 100,
                show: true
            },
            {
                accessor: 'achDetailId',
                className: ['cursor-pointer'],
                label: 'Deposit Detail ID',
                minWidth: 150,
                show: false
            },
            {
                accessor: 'processMethod',
                transformData: transformProcessMethodCell,
                className: ['cursor-pointer'],
                Filter: ({ filter, onChange }) => (<FilterWithMapper filter={filter} mapper={processMethodMapper} onChange={onChange} />),
                label: 'Method',
                minWidth: 100,
                show: false
            },
            {
                accessor: 'cardHolder',
                transformData: transformUppercaseCell,
                className: ['cursor-pointer'],
                label: 'Card Holder',
                minWidth: 140,
                show: false
            },
            {
                accessor: 'cardType',
                transformData: transformCardTypeCell,
                className: ['cursor-pointer'],
                Filter: ({ filter, onChange }) => (<FilterWithMapper filter={filter} mapper={cardTypesMapper} onChange={onChange} />),
                label: 'Card Type',
                minWidth: 120,
                show: true
            },
            {
                accessor: 'cardNumber',
                transformData: transformEllipsisNumberCell,
                className: ['cursor-pointer'],
                label: 'Card Num',
                title: toolTipContent,
                minWidth: 120,
                show: true
            },
            {
                accessor: 'amount',
                Cell: (props) => <ColorDollarAmountCell amount={props.value} currencyCode={getCurrencyCode(props.original.currencyId)} overrideText="Pending..." />,
                className: ['right-justify', 'cursor-pointer'],
                Filter: ({ filter, onChange }) => (<AmountFilter filter={filter} onChange={onChange} />),
                label: 'Amount',
                minWidth: 115,
                show: true
            },
            {
                accessor: 'currencyId',
                Cell: (props) => transformUppercaseCell({ value: getCurrencyCode(props.value) }),
                transformData: transformCurrencyCodeCell,
                className: ['cursor-pointer'],
                Filter: ({ filter, onChange }) => (<CurrencyCodeFilter filter={filter} onChange={onChange} />),
                label: 'Currency',
                minWidth: 110,
                show: false
            },
            {
                accessor: 'transactionStatus',
                transformData: transformTransactionStatusCell,
                className: ['cursor-pointer'],
                filterable: false,
                label: 'Status',
                minWidth: 110,
                show: false
            },
            {
                accessor: 'approveByTime',
                Cell: (props) => getHoursLeftToAutoApprove(_.get(props.original, 'transactionDate')),
                transformData: this.transformDateToAutoApproveCell,
                className: ['cursor-pointer'],
                filterable: false,
                sortable: false,
                label: 'Auto Approve Time',
                minWidth: 180,
                show: true
            },
            {
                accessor: 'transactionType',
                transformData: transformTransactionTypeCell,
                className: ['cursor-pointer'],
                Filter: ({ filter, onChange }) => (<FilterWithMapper filter={filter} mapper={transactionTypeMapper} onChange={onChange} />),
                label: 'Type',
                minWidth: 105,
                show: true
            },
            {
                accessor: 'authCode',
                className: ['cursor-pointer'],
                excelStringField: true,
                label: 'Auth Code',
                minWidth: 120,
                show: false
            },
            {
                accessor: 'plugin.orderNumber',
                className: ['cursor-pointer'],
                excelStringField: true,
                label: 'Order ID',
                minWidth: 100,
                show: false
            },
            {
                accessor: 'gateway.refNumber',
                className: ['cursor-pointer'],
                excelStringField: true,
                label: 'Ref Num',
                minWidth: 100,
                show: false
            },
            {
                accessor: 'gateway.batchRef',
                className: ['cursor-pointer'],
                excelStringField: true,
                label: 'Gateway Batch ID',
                minWidth: 170,
                show: false
            },
            {
                accessor: 'plugin.invoice',
                className: ['cursor-pointer'],
                excelStringField: true,
                label: 'Invoice',
                minWidth: 100,
                show: false
            },
            {
                accessor: 'plugin.description',
                className: ['cursor-pointer'],
                label: 'Description',
                minWidth: 140,
                show: false
            },
            {
                accessor: 'customer.customerRef',
                className: ['cursor-pointer'],
                excelStringField: true,
                label: 'Customer ID',
                minWidth: 150,
                show: false
            },
            {
                accessor: 'plugin.userId',
                className: ['cursor-pointer'],
                excelStringField: true,
                label: 'User ID',
                minWidth: 100,
                show: false
            },
            {
                accessor: 'gateway.gatewayType',
                transformData: transformGatewayTypeCell,
                className: ['cursor-pointer'],
                Filter: ({ filter, onChange }) => (<FilterWithMapper filter={filter} mapper={getSortedGatewayAndApmMapperArray(this.props.gateways, this.props.apms)} onChange={onChange} />),
                label: 'Gateway',
                minWidth: 105,
                show: false
            },
            {
                accessor: 'merchantId',
                className: ['cursor-pointer'],
                excelStringField: true,
                filterable: false,
                label: 'Merchant ID',
                minWidth: 145,
                show: false
            },
            {
                accessor: 'foreignProcessingCurrency.currencyId',
                transformData: dataTransform.currencyCode,
                className: ['cursor-pointer'],
                Filter: ({ filter, onChange }) => (<CurrencyCodeFilter filter={filter} onChange={onChange} allowAllCurrencies />),
                label: 'Foreign Currency',
                minWidth: 180,
                show: false,
                transformType: 'currencyCode'
            },
            {
                accessor: 'foreignProcessingCurrency.amount',
                Cell: (props) => <ColorDollarAmountCell amount={props.value} currencyCode={getCurrencyCode(_.get(props, 'original.foreignProcessingCurrency.currencyId'))} overrideText=" " />,
                className: ['right-justify', 'cursor-pointer'],
                Filter: ({ filter, onChange }) => (<AmountFilter filter={filter} onChange={onChange} />),
                label: 'Foreign Amount',
                minWidth: 160,
                show: false
            }
        ];
    }

    componentDidUpdate(prevProps) {
        toastIfError(prevProps, this.props, 'bulkVoidError');
        toastIfError(prevProps, this.props, 'bulkCaptureError');

        if (doesPropExistAndHasItChanged(prevProps, this.props, 'isBulkVoidSuccessful') && this.props.isBulkVoidSuccessful === true) {
            toast('Bulk Void Successful!', { type: 'success' });
            this.refetchData();
        }

        if (doesPropExistAndHasItChanged(prevProps, this.props, 'isBulkCaptureSuccessful') && this.props.isBulkCaptureSuccessful === true) {
            toast('Bulk Capture Successful!', { type: 'success' });
            this.refetchData();
        }

        if (doesPropExistAndHasItChanged(prevProps, this.props, 'bulkVoidError')
            || doesPropExistAndHasItChanged(prevProps, this.props, 'bulkCaptureError')) {
            this.refetchData();
        }
    }

    shouldRowBeCheckable(row) {
        return this.props.doesUserHaveWriteAccess(_.get(row, 'merchantId'));
    }

    transformDateToAutoApproveCell(props) {
        const transactionDate = _.get(props.original, 'transactionDate');
        const dateToAutoApprove = moment(transactionDate).add(hoursToAutoApproveTransaction, 'hours');

        return prettifyDate(dateToAutoApprove, { dateTimeFormatOutput: 'YYYY-MM-DD h:mm A' });
    }

    resetNeedsRefetch() {
        this.setState({ needsRefetch: false });
    }

    refetchData() {
        this.setState({ needsRefetch: true });
    }

    renderHeaderComponent() {
        const linkProps = {
            className: 'kount-rules-link',
            target: '_blank',
            rel: 'noopener noreferrer'
        };

        return (
            <span>
                Manage{' '}
                <a
                    href="https://awc.kount.net/dmc/rules/list.html"
                    {...linkProps}
                >
                    Kount
                </a>
                {'/ '}
                <a
                    href={`${config.kount360TransactionUrl}/policy-management/policy-sets`}
                    {...linkProps}
                >
                    Kount360
                </a>
                {' '}Rules
            </span>
        );
    }

    render() {
        const dateKey = 'transactionDate';
        const initialEndDate = moment().utc().endOf('day');
        const initialStartDate = moment().utc().subtract(3, 'day');

        const bulkActions = [{
            ButtonLabel: 'Approve',
            confirmationText: 'Are you sure you would like to approve these transactions?',
            loading: this.props.isBulkCaptureLoading,
            onClick: bulkCaptureTransactions
        }, {
            ButtonLabel: 'Decline',
            confirmationText: 'Are you sure you would like to decline these transactions?',
            loading: this.props.isBulkVoidLoading,
            onClick: bulkVoidTransactions
        }];

        return (this.props.doAnySelectedMerchantsHaveFraud
            ? (
                <>
                    {this.props.flags?.resellerPhase1 && this.props.self?.accountType === 'RESELLER' && <InPageMerchantSelector />}
                    <Table
                        bulkActions={bulkActions}
                        className="emvio-fraud-table"
                        columnsToNotClick={['Action']}
                        countSelector={getFraudCount}
                        dateKey={dateKey}
                        dataSelector={getFraud}
                        defaultColumnsAndProps={this.defaultColumnsAndProps}
                        defaultFiltered={[
                            {
                                id: 'kount.status',
                                value: 'R'
                            },
                            {
                                id: 'transactionStatus',
                                value: 11
                            }
                        ]}
                        defaultSorted={[{
                            desc: true,
                            id: dateKey
                        }]}
                        errorMessageSelector={getFraudErrorMessage}
                        fetchData={fetchFraud}
                        fetchUrl={fetchUrl}
                        filterable={true}
                        hasMoreSelector={getHasMore}
                        hasNoCount={true}
                        HeaderComponent={this.renderHeaderComponent}
                        initialEndDate={initialEndDate}
                        initialStartDate={initialStartDate}
                        isDataLoadingSelector={getIsFraudLoading}
                        itemKey="id"
                        ListItemComponent={FraudListItem}
                        needsRefetch={this.state.needsRefetch}
                        noDataText="No Transactions To Review"
                        pagesSelector={getPages}
                        resetNeedsRefetch={this.resetNeedsRefetch}
                        selectRow={selectTransaction}
                        shouldAddCheckboxColumn
                        shouldHideDateRange
                        shouldRowBeCheckable={this.shouldRowBeCheckable}
                        toggleExportMessageVisible={() => { }}
                        uniqueTableName="Fraud"
                        timezone={this.props.timezone}
                    />
                </>
            ) : <Marketing />
        );
    }
}

function mapStateToProps(state) {
    return {
        bulkCaptureError: getBulkCaptureErrorMessage(state),
        bulkVoidError: getBulkVoidErrorMessage(state),
        doAnySelectedMerchantsHaveFraud: getDoAnySelectedMerchantsHaveFraud(state),
        doesUserHaveWriteAccess: (merchantId) => getDoesUserHaveWriteAccessForMerchant(state, merchantId),
        isBulkCaptureLoading: getIsBulkCaptureLoading(state),
        isBulkCaptureSuccessful: getIsBulkCaptureSuccessful(state),
        isBulkVoidLoading: getIsBulkVoidLoading(state),
        isBulkVoidSuccessful: getIsBulkVoidSuccessful(state),
        timezone: getTimezone(state),
        gateways: getGateways(state),
        apms: getApms(state),
        self: getSelf(state)
    };
}

export default withLDConsumer()(connect(mapStateToProps, null)(Fraud));
